import React from 'react';
import { Link } from 'react-router-dom';
import { FlameIcon, PlusIcon } from 'lucide-react';
import { ProgressBar } from '../ui/ProgressBar';
import { useActivityPlan } from '../../contexts/ActivityPlanContext';
import { activityById, focusOptions } from '../../data/activityCatalog';
import { actualForPeriod, actualToday, dailyTarget, percentOf } from '../../utils/activityProgress';
interface DayScoreBannerProps {
  firstName: string;
  dateLabel: string;
  onLogActivity: () => void;
}
export function DayScoreBanner({
  firstName,
  dateLabel,
  onLogActivity
}: DayScoreBannerProps) {
  const {
    plan,
    activeGoals,
    logs
  } = useActivityPlan();
  const scored = activeGoals.map(goal => {
    const definition = activityById[goal.category];
    const isDaily = definition.cadence === 'daily';
    const target = isDaily ? dailyTarget(goal, plan.workdays) : goal.weeklyTarget;
    const actual = isDaily ? actualToday(logs, goal.category) : actualForPeriod(logs, goal.category, 'week');
    return {
      target,
      actual,
      percent: percentOf(actual, target)
    };
  });
  const completion = scored.length ? Math.round(scored.reduce((total, row) => total + row.percent, 0) / scored.length) : 0;
  const met = scored.filter(row => row.actual >= row.target).length;
  const focusLabel = focusOptions.find(option => option.id === plan.focus)?.label ?? 'Custom focus';
  const status = completion >= 100 ? {
    label: 'Perfect day complete',
    classes: 'bg-success-50 text-success-700 ring-success-200'
  } : completion >= 60 ? {
    label: 'On pace',
    classes: 'bg-brand-50 text-brand-700 ring-brand-200'
  } : {
    label: 'Behind pace',
    classes: 'bg-warn-50 text-warn-700 ring-warn-200'
  };
  return <section className="rounded-2xl border border-navy-100 bg-white p-5 shadow-card sm:p-6" data-id="element-59" data-mp="2xdxlz5k60xwt">
      <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-60" data-mp="1gw9taxnp8pvw">
        <div className="min-w-0" data-id="element-61" data-mp="1lxh1uyxo1t3m">
          <p className="text-xs font-semibold uppercase tracking-wide text-navy-500" data-id="element-62" data-mp="1lul5vg5dpeht">
            {dateLabel}
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-navy-900" data-id="element-63" data-mp="3jki5qxd4aake">
            Good morning, {firstName}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-64" data-mp="3esi0qqi6wrgy">
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${status.classes}`} data-id="element-65" data-mp="39uq5pnm90p8j">
              {status.label}
            </span>
            {logs.length > 0 ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-navy-600" data-id="element-66" data-mp="30vv1gh2rlp7v">
                <FlameIcon className="h-3.5 w-3.5 text-warn-500" aria-hidden="true" data-id="element-67" data-mp="2vwrwewmsbizw" />
                6-day logging streak
              </span> : null}
            <span className="text-xs text-navy-500" data-id="element-68" data-mp="p47wl93rw6el">
              Focus: {focusLabel} · {plan.lastUpdatedLabel}
            </span>
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-2 sm:items-end" data-id="element-69" data-mp="wn7dn83rhlus">
          <button type="button" onClick={onLogActivity} className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 text-sm font-semibold text-white hover:bg-brand-700" data-id="element-70" data-mp="11m1ui4i650c9">
            <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-71" data-mp="1bepnsp9xwm68" />
            Log activity
          </button>
          <Link to="/progress" className="text-center text-xs font-semibold text-brand-600 hover:underline" data-id="element-72" data-mp="dr8yrszphk5p">
            View weekly scoreboard
          </Link>
        </div>
      </div>

      <div className="mt-5" data-id="element-73" data-mp="slvnn1gz6ym2">
        <div className="flex items-end justify-between gap-3" data-id="element-74" data-mp="sn6lylrnje5s">
          <p className="text-sm text-navy-600" data-id="element-75" data-mp="1z2hdqvoekxpz">
            <span className="text-xl font-bold tabular-nums text-navy-900" data-id="element-76" data-mp="1z1pgzhj2kty4">
              {met}
            </span>{' '}
            of {scored.length} activity goals met today
          </p>
          <p className="text-sm font-semibold tabular-nums text-navy-700" data-id="element-77" data-mp="21nf17ftcavgg">
            {completion}%
          </p>
        </div>
        <ProgressBar className="mt-2" value={completion} size="lg" tone={completion >= 100 ? 'success' : 'brand'} label="Perfect day completion" data-id="element-78" data-mp="1xn4xk9t0sh4r" />
      </div>
    </section>;
}